import React from "react";
import { extractBprBpmnXml, getBprBpmnSource } from "../../services/bprRedesignService";
import {
  getNvaElimination,
  getKpiBasis,
  getKpiHeadline,
  getHeadlineMetric,
  isNvaDrivenRow,
  humanizeHeuristicName,
} from "../../utils/nvaElimination";
import {
  NvaEliminationCard,
  NvaSourceChip,
  KpiBasisNote,
  CombinedImprovementBreakdown,
} from "./NvaEliminationBits";
import {
  getProjection,
  getFallbackChildren,
  splitAppliedRows,
  DEFAULT_INCLUDE_SUBPROCESS,
} from "../../utils/subprocessRedesign";
import { ChildChip, ProjectionBanner, FallbackChildrenNote } from "./SubprocessProjectionBits";

const SECTION_STYLE = {
  applied: { bg: "var(--green-tint-100)", ink: "var(--green-ink-800)" },
  not_applied: { bg: "var(--slate-100)", ink: "var(--slate-600)" },
};

function dqChipsSmall(dims) {
  return (dims || []).map((k) => (
    <span key={k} style={{ fontSize: 9, fontWeight: 700, color: "var(--slate-600)", background: "var(--slate-100)", borderRadius: 999, padding: "1px 6px", marginRight: 3, textTransform: "capitalize" }}>{k}</span>
  ));
}

// Positive reduction_pct = the redesign lowered that metric (an improvement),
// per FRONTEND_GUIDE.md §4 — ▼ green for improved, ▲ red for a regression;
// the model can legitimately trade one metric for the other, so a regression
// is rendered as a badge, never an error.
function pctBadge(v) {
  if (v == null) return "—";
  const improved = v > 0;
  const color = v === 0 ? "#6b7280" : improved ? "#059669" : "#dc2626";
  const glyph = v === 0 ? "–" : improved ? "▼" : "▲";
  return <span style={{ fontWeight: 800, color }}>{glyph} {Math.abs(v).toFixed(1)}%</span>;
}

/**
 * Renders the BPR Redesign result (POST /cms/bpr-redesign/{id}) — the
 * external, already-trained ML model pipeline, a sibling to the deterministic
 * /cms/optimize heuristics matrix and the RL agent's own redesign. Unlike
 * both of those, there is nothing to configure: one call returns the full
 * result, so this panel is just a Run button plus the result render.
 *
 * State (result/running/etc.) is owned by the page (useBprRedesignData lifted
 * there) and passed in as props, not fetched locally — the last run's
 * to_be.bpmn_xml needs to survive this panel unmounting when the analyst
 * switches to the BPMN tab's "BPR BPMN" view.
 */
export default function BprRedesignPanel({
  processId, money, fmtMin, fixDecimals,
  healthy, checkHealth,
  result, running, runError, run,
  onViewBpmn,
}) {
  React.useEffect(() => {
    checkHealth?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On by default (see DEFAULT_INCLUDE_SUBPROCESS): a parent whose time sits inside
  // its children reports little change without it. Costs one extra call to the
  // external model per sub-process, plus one roll-up call.
  const [includeSubprocesses, setIncludeSubprocesses] = React.useState(DEFAULT_INCLUDE_SUBPROCESS);

  if (!processId) return null;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        <button
          type="button"
          className="primary-btn sm"
          disabled={running}
          onClick={() => run(processId, { includeSubprocessRedesigns: includeSubprocesses })}
        >
          {running ? "Redesigning process…" : result ? "Re-run" : "Run BPR Redesign"}
        </button>
        <span style={{ fontSize: 11, color: "var(--gray-400)" }}>no goal or episodes to pick — the model decides everything</span>
        {healthy === false && (
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--amber-ink-700)", background: "var(--amber-tint-50)", border: "1px solid var(--amber-tint-200)", borderRadius: 999, padding: "1px 8px" }}>
            External model unreachable
          </span>
        )}
      </div>

      <label
        style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 14, fontSize: 12, color: "var(--gray-700)", cursor: "pointer" }}
        title="Sub-processes are frozen during a normal run, so a parent whose time mostly sits inside its children reports little change. This redesigns them too and rolls the effect into this process."
      >
        <input
          type="checkbox"
          checked={includeSubprocesses}
          disabled={running}
          onChange={(e) => setIncludeSubprocesses(e.target.checked)}
        />
        <span>Also redesign this process&apos;s sub-processes</span>
        <span style={{ fontSize: 11, color: "var(--gray-400)" }}>
          adds one model call per sub-process, plus one roll-up · results are proposals, not saved to CMS
        </span>
      </label>

      {running && (
        <div className="no-results">
          {includeSubprocesses
            ? "Sending this process and each of its sub-processes to the external BPR Redesign model…"
            : "Sending this process to the external BPR Redesign model…"}
        </div>
      )}

      {runError && (
        <div style={{ padding: "12px 16px", marginBottom: 14, background: "var(--red-tint-50)", border: "1px solid var(--red-tint-300)", borderRadius: 10, color: "var(--red-ink-800)", fontSize: 13 }}>
          <strong>BPR redesign failed:</strong> {runError}
        </div>
      )}

      {!result && !running && !runError && (
        <div className="no-results">Run the BPR Redesign model to see how it would redesign this process.</div>
      )}

      {result && (
        <BprResult
          result={result}
          money={money}
          fmtMin={fmtMin}
          fixDecimals={fixDecimals}
          onViewBpmn={onViewBpmn}
        />
      )}
    </div>
  );
}

/**
 * The model's own raw predictions, kept separate from what the run actually applied.
 * The two diverge by design now: on process 1979 the model predicted
 * task_elimination: false, yet the applied table shows it applied — because CMS
 * classified the step non-value-adding and it was removed before the model ran.
 * Folded into a details block: it is provenance, not a result, and must never sit
 * where it could be mistaken for one.
 */
function PredictedLabels({ labels, table }) {
  if (!labels || typeof labels !== "object") return null;

  const appliedNames = new Set(
    (Array.isArray(table) ? table : [])
      .filter((s) => s.section === "applied")
      .flatMap((s) => (s.rows || []).map((r) => String(r.heuristicName || "").toLowerCase())),
  );

  const entries = Object.entries(labels);
  const divergent = entries.filter(([name, predicted]) => appliedNames.has(String(name).toLowerCase()) !== Boolean(predicted));

  return (
    <details style={{ marginBottom: 16, fontSize: 11.5 }}>
      <summary style={{ cursor: "pointer", color: "var(--gray-500)", fontWeight: 700 }}>
        What the model itself predicted
        {divergent.length > 0 && ` — ${divergent.length} differ${divergent.length === 1 ? "s" : ""} from what was applied`}
      </summary>
      <div style={{ marginTop: 6, padding: "8px 10px", background: "var(--gray-50)", border: "1px solid var(--slate-100)", borderRadius: 8, color: "var(--slate-600)" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
          {entries.map(([name, predicted]) => (
            <span
              key={name}
              style={{
                fontSize: 10, fontWeight: 700, borderRadius: 999, padding: "1px 8px",
                background: predicted ? "var(--green-tint-100)" : "var(--slate-100)",
                color: predicted ? "var(--green-ink-800)" : "var(--slate-400)",
                border: `1px solid ${predicted ? "var(--green-tint-300)" : "var(--slate-200)"}`,
              }}
            >
              {humanizeHeuristicName(name)}
            </span>
          ))}
        </div>
        <div style={{ fontSize: 10.5, color: "var(--gray-400)" }}>
          These are the model&apos;s raw label predictions. A heuristic can be applied without
          being predicted here — an elimination driven by the CMS value analysis is applied
          before the model is consulted at all.
        </div>
      </div>
    </details>
  );
}

/**
 * Heading for one child's block of heuristics. BPR carries no per-child
 * parent_impact (the model rolls the parent up itself rather than apportioning by
 * branch — see `subprocess_redesign_projection`'s own note), so unlike the RL panel's
 * equivalent this can only show what changed INSIDE the child, sourced from
 * `childHeadline` (that child's own `headline` on the projection). What that bought
 * the parent lives in the KPI badges and CombinedImprovementBreakdown above, not here.
 */
function ChildGroupHeader({ childName, childHeadline, rowCount, fmtMin, money }) {
  const time = childHeadline?.headline?.cycle_time_minutes;
  const cost = childHeadline?.headline?.labor_cost_per_case;
  return (
    <tr>
      <td colSpan={5} style={{ padding: "8px 10px", background: "var(--purple-tint-50)", borderTop: "1px solid var(--violet-tint-100)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <ChildChip name={childName} />
          <span style={{ fontSize: 10.5, color: "var(--gray-400)" }}>
            {rowCount} heuristic{rowCount === 1 ? "" : "s"} applied inside this sub-process
          </span>
        </div>
        {time && (
          <div style={{ marginTop: 5, fontSize: 11.5, color: "var(--gray-700)" }}>
            <span style={{ fontSize: 9.5, fontWeight: 700, color: "var(--gray-400)", textTransform: "uppercase" }}>Within the sub-process: </span>
            {fmtMin(time.as_is)} → {fmtMin(time.to_be)}
            {cost && <span style={{ color: "var(--gray-500)" }}> · {money(cost.as_is)} → {money(cost.to_be)}</span>}
          </div>
        )}
      </td>
    </tr>
  );
}

// One heuristic row, shared by the parent's own rows and a child group's rows.
// `childName` is set only for source="subprocess" rows.
function HeuristicRow({ row, sectionKey, idx, fixDecimals, childName }) {
  // Rule N2 — an applied row carrying nva_eliminated_task_ids was not the model's
  // choice: CMS classified those steps non-value-adding and they were stripped
  // before the model ran.
  const nvaDriven = isNvaDrivenRow(row);
  const accent = childName ? "#7c3aed" : nvaDriven ? "#0284c7" : "#16a34a";
  const bg = childName
    ? "#fdfcff"
    : nvaDriven
      ? "#f0f9ff"
      : sectionKey === "applied"
        ? "#f0fdf4"
        : idx % 2
          ? "#fcfcfd"
          : "#fff";
  return (
    <tr style={{ verticalAlign: "top", background: bg }}>
      <td style={{ padding: "8px 10px", fontWeight: 700, whiteSpace: "nowrap", position: "sticky", left: 0, background: "inherit", zIndex: 1, ...(row.isApplied ? { boxShadow: `inset 3px 0 0 ${accent}` } : {}) }}>
        {row.isApplied && <span aria-hidden="true" style={{ color: accent, fontWeight: 800, marginRight: 4 }}>✓</span>}
        {/* The model names heuristics in its own snake_case vocabulary; every other
            tab uses prose. Left raw, the same heuristic reads as two different things. */}
        {humanizeHeuristicName(row.heuristicName)}
        <div style={{ fontSize: 9.5, fontWeight: 400, color: "var(--gray-400)", textTransform: "uppercase" }}>{String(row.category || "").replace(/_/g, " ")}</div>
        {childName && (
          <div style={{ marginTop: 3 }}>
            <ChildChip name={childName} />
          </div>
        )}
        {nvaDriven && (
          <div style={{ marginTop: 3 }}>
            <NvaSourceChip />
          </div>
        )}
      </td>
      <td style={{ padding: "8px 10px", color: "var(--gray-700)", maxWidth: 240 }}>{row.trigger_rule}</td>
      <td style={{ padding: "8px 10px", color: "var(--gray-500)", maxWidth: 320 }}>{fixDecimals ? fixDecimals(row.measured_verdict) : row.measured_verdict}</td>
      <td style={{ padding: "8px 10px", color: "var(--gray-700)", maxWidth: 200 }}>
        {(row.taskApplied || []).length > 0
          ? row.taskApplied.map((t) => t.task_name || t.gateway_name).join(", ")
          : "—"}
      </td>
      <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{dqChipsSmall(row.dq_dimensions)}</td>
    </tr>
  );
}

function BprResult({ result, money, fmtMin, fixDecimals, onViewBpmn }) {
  const {
    cost_time_comparison: ctc,
    heuristic_table: table,
    subprocesses,
    combined_improvement: combined,
    predicted_labels: predictedLabels,
    schema_ok: schemaOk,
    validation_problems: validationProblems,
  } = result;

  const asIs = ctc?.as_is;
  const toBe = ctc?.to_be;
  const bpmnXml = extractBprBpmnXml(result);
  const bpmnSource = getBprBpmnSource(result);

  const nva = getNvaElimination(result);
  const kpiBasis = getKpiBasis(result);
  const headline = getKpiHeadline(result);
  const headlineTime = getHeadlineMetric(headline, "cycle_time_minutes");
  const headlineCost = getHeadlineMetric(headline, "labor_cost_per_case");

  const projection = getProjection(result);
  const fallbackChildren = getFallbackChildren(projection);
  // Per-child headline (own as_is/to_be/reduction_pct), keyed for the group header
  // below. BPR sends no per-child parent_impact — the model rolls the parent up
  // itself rather than apportioning by branch — so this child-own figure is the only
  // per-child number available; it answers "what changed inside the child", not
  // "what that bought the parent".
  const childHeadlineById = new Map(
    (Array.isArray(projection?.children) ? projection.children : [])
      .filter((c) => c?.status === "redesigned")
      .map((c) => [c.child_process_id, c]),
  );

  // kpi_basis.headline is the full result: the CMS NVA elimination compounded with
  // the model's own redesign. cost_time_comparison covers the model's share alone and
  // understates the run — on process 1979 it reads 3.2%/18.1% against 7.1%/19.9%.
  // Top-level *_reduction_pct is null on current responses, so it can only be a
  // fallback, never the primary read.
  const timePct = headlineTime ? headlineTime.reduction_pct
    : result.cycle_time_reduction_pct ?? ctc?.cycle_time_reduction_pct;
  const costPct = headlineCost ? headlineCost.reduction_pct
    : result.labor_cost_reduction_pct ?? ctc?.labor_cost_reduction_pct;

  // The AS-IS in cost_time_comparison is the process AFTER the NVA steps were
  // stripped — it is what was submitted to the model, not what is stored in CMS.
  // Labelling it plain "AS-IS" hides a whole step of the improvement.
  const trueAsIs = nva?.as_is || null;
  const asIsLabel = nva ? "After NVA removal" : "AS-IS";

  return (
    <div>
      {/* Without the flag (or on a run that predates this capability) the model sees
          each child only as a rolled-up cycle-time/cost slot — it never redesigns
          them, so the figures below hold every child at its stored state. With the
          flag, ProjectionBanner/FallbackChildrenNote below replace this note. */}
      {Array.isArray(subprocesses) && subprocesses.length > 0 && !projection && (
        <div style={{ marginBottom: 14, fontSize: 12.5, color: "var(--slate-600)" }}>
          Contains: {subprocesses.map((sp, i) => (
            <span key={sp.child_process_id}>
              <strong>{sp.child_process_name}</strong>{i < subprocesses.length - 1 ? ", " : ""}
            </span>
          ))}
          {" "}— these are counted at their current figures; check &ldquo;Also redesign this
          process&apos;s sub-processes&rdquo; above to change that.
        </div>
      )}

      <ProjectionBanner projection={projection} />
      <FallbackChildrenNote items={fallbackChildren} />

      <NvaEliminationCard
        nva={nva}
        money={money}
        fmtMin={fmtMin}
        costKey="labor_cost_per_case"
        costLabel="Labor cost per case"
      />

      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--gray-500)", textTransform: "uppercase" }}>Cycle time</div>
          <div style={{ fontSize: 18 }}>{pctBadge(timePct)}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--gray-500)", textTransform: "uppercase" }}>Labor cost per case</div>
          <div style={{ fontSize: 18 }}>{pctBadge(costPct)}</div>
        </div>
      </div>

      <KpiBasisNote basis={kpiBasis} headline={headline} />

      <CombinedImprovementBreakdown
        combined={combined}
        subprocessProjection={projection}
        labels={{
          nva_elimination: "Removing non-value-adding steps (CMS)",
          model_redesign: "The model's own redesign",
        }}
      />

      <div style={{ overflowX: "auto", marginBottom: 20 }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 520, fontSize: 12 }}>
          <thead>
            <tr>
              {["", ...(trueAsIs ? ["AS-IS (stored)"] : []), asIsLabel, "TO-BE"].map((h) => (
                <th key={h || "label"} style={{ padding: "6px 10px", fontSize: 10.5, color: "var(--gray-500)", textAlign: h ? "right" : "left", textTransform: "uppercase", borderBottom: "2px solid var(--gray-200)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* `stored` is only ever populated for the two metrics nva_elimination
                carries. The rest render an em-dash rather than repeating the
                post-elimination figure, which would silently equate the two. */}
            {[
              { label: "Cycle time", key: "cycle_time_minutes", fmt: fmtMin, stored: trueAsIs?.cycle_time_minutes },
              { label: "Processing time", key: "processing_time_minutes", fmt: fmtMin },
              { label: "Waiting time", key: "waiting_time_minutes", fmt: fmtMin },
              { label: "Rework time", key: "rework_time_minutes", fmt: fmtMin },
              { label: "Cycle-time efficiency", key: "cycle_time_efficiency_percent", fmt: (v) => (v != null ? `${v.toFixed(1)}%` : "—") },
              { label: "Labor cost per case", key: "labor_cost_per_case", fmt: money, stored: trueAsIs?.labor_cost_per_case },
            ].map((row) => (
              <tr key={row.key}>
                <td style={{ padding: "6px 10px", fontWeight: 600 }}>{row.label}</td>
                {trueAsIs && (
                  <td style={{ padding: "6px 10px", textAlign: "right", color: "var(--sky-ink-800)" }}>
                    {row.stored != null ? row.fmt(row.stored) : "—"}
                  </td>
                )}
                <td style={{ padding: "6px 10px", textAlign: "right" }}>
                  {asIs?.[row.key] != null ? row.fmt(asIs[row.key]) : "—"}
                </td>
                <td style={{ padding: "6px 10px", textAlign: "right", fontWeight: 700 }}>
                  {toBe?.[row.key] != null ? row.fmt(toBe[row.key]) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {trueAsIs && (
          <div style={{ marginTop: 6, fontSize: 10.5, color: "var(--gray-400)" }}>
            &ldquo;{asIsLabel}&rdquo; is what was submitted to the model — the stored process with the
            CMS-flagged non-value-adding step{(nva.eliminated || []).length === 1 ? "" : "s"} already
            taken out. The model never saw the stored column.
          </div>
        )}
      </div>

      {/* `schema_ok`/`validation_problems` describe the RAW upstream model response,
          which routinely omits bpmn_xml — the backend then generates the diagram
          itself and sets bpmn_xml_source: "generated". Keying the notice off schema_ok
          printed "Diagram unavailable (missing/empty field: bpmn_xml)" directly above
          "The BPR-redesigned diagram is ready". Only the absence of usable XML means
          the diagram is unavailable. */}
      {(bpmnSource === "missing" || !bpmnXml) && (
        <div style={{ marginBottom: 16, fontSize: 11.5, color: "var(--gray-400)" }}>
          Diagram unavailable for this process{Array.isArray(validationProblems) && validationProblems.length > 0 ? ` (${validationProblems.join(", ")})` : ""}.
        </div>
      )}

      {bpmnXml && schemaOk === false && Array.isArray(validationProblems) && validationProblems.length > 0 && (
        <div style={{ marginBottom: 16, fontSize: 11.5, color: "var(--gray-400)" }}>
          The model&apos;s raw response was incomplete ({validationProblems.join(", ")}); the diagram
          below was generated from the process data instead.
        </div>
      )}

      <PredictedLabels labels={predictedLabels} table={table} />

      {Array.isArray(table) && table.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          {childHeadlineById.size > 0 && (
            <div style={{ fontSize: 11.5, color: "var(--gray-500)", marginBottom: 6 }}>
              Grouped below: this process&apos;s own heuristics first, then each sub-process&apos;s.
            </div>
          )}
          {table.map((section) => {
            const style = SECTION_STYLE[section.section] || SECTION_STYLE.not_applied;
            const { parentRows, childGroups } = splitAppliedRows(section.rows);
            return (
              <div key={section.section} style={{ marginBottom: 14 }}>
                <div style={{ padding: "7px 10px", fontSize: 11.5, fontWeight: 800, background: style.bg, color: style.ink, borderRadius: "8px 8px 0 0" }}>
                  {section.title}
                </div>
                <div style={{ fontSize: 11.5, color: "var(--gray-500)", padding: "6px 10px", background: "var(--gray-50)", border: "1px solid var(--slate-100)", borderTop: "none" }}>
                  {section.description}
                </div>
                <div style={{ overflowX: "auto", border: "1px solid var(--slate-100)", borderTop: "none", borderRadius: "0 0 10px 10px" }}>
                  <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 820, fontSize: 12 }}>
                    <thead>
                      <tr>
                        {["Heuristic", "Trigger rule", "Verdict", "Task(s)", "DQ dimensions"].map((h, i) => (
                          <th key={h} style={{ padding: "8px 10px", fontSize: 10.5, color: "var(--gray-500)", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "2px solid var(--gray-200)", background: "var(--slate-50)", position: i === 0 ? "sticky" : undefined, left: i === 0 ? 0 : undefined, zIndex: i === 0 ? 1 : undefined }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parentRows.map((row, idx) => (
                        <HeuristicRow key={`parent-${row.heuristicId}`} row={row} sectionKey={section.section} idx={idx} fixDecimals={fixDecimals} />
                      ))}

                      {parentRows.length === 0 && childGroups.length > 0 && (
                        <tr>
                          <td colSpan={5} style={{ padding: "8px 10px", fontSize: 11.5, color: "var(--gray-500)", fontStyle: "italic", background: "var(--color-surface)" }}>
                            None applied to this process directly.
                          </td>
                        </tr>
                      )}

                      {childGroups.map((g) => (
                        <React.Fragment key={`child-${g.key}`}>
                          <ChildGroupHeader
                            childName={g.childProcessName}
                            childHeadline={childHeadlineById.get(g.childProcessId)}
                            rowCount={g.rows.length}
                            fmtMin={fmtMin}
                            money={money}
                          />
                          {g.rows.map((row, idx) => (
                            <HeuristicRow
                              key={`child-${g.key}-${row.heuristicId}-${idx}`}
                              row={row}
                              sectionKey={section.section}
                              idx={idx}
                              fixDecimals={fixDecimals}
                              childName={g.childProcessName}
                            />
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {bpmnXml && onViewBpmn && (
        <div style={{ padding: "10px 14px", background: "var(--teal-tint-50)", border: "1px solid var(--teal-tint-200)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <span style={{ fontSize: 12.5, color: "var(--teal-ink-deep)" }}>
            The BPR-redesigned diagram is ready{bpmnSource === "generated" ? " (generated from the process data)" : ""}.
          </span>
          <button type="button" className="secondary-btn sm" onClick={onViewBpmn}>
            View in BPMN tab →
          </button>
        </div>
      )}
    </div>
  );
}
